import React, { useCallback } from "react";
import { Alert, Button, Form, Input, InputNumber, Select, Space, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { SectionPanelProps } from "./SectionTypes";
import { useJsonSection } from "./useJsonSection";
import { ClientConfig } from "../../../model/client/ClientSettings";
import { BannerRule, BannerSeverity, parseBannerRules } from "../../../components/membership/profile/bannerRules";
import { CopyFromClientButton } from "./CopyFromClientModal";

const { Text } = Typography;

export const BANNERS_RULES_KEY = 'banners.rules';

const SEVERITY_OPTIONS: { value: BannerSeverity; label: string }[] = [
    { value: 'info', label: 'Info' },
    { value: 'success', label: 'Success' },
    { value: 'warning', label: 'Warning' },
    { value: 'error', label: 'Error (elevated)' },
];

const newRule = (): BannerRule => ({
    id: `rule-${Date.now()}`,
    type: 'membershipExpiry',
    thresholdDays: 14,
    severity: 'warning',
    message: 'Membership expires in {{daysUntilExpiry}} days',
});

// Settings panel for config-driven profile banners (banners.rules). Today only
// the membershipExpiry rule type is evaluated; the {{daysUntilExpiry}} token is
// interpolated into the message at render time.
export const BannersSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const extract = useCallback(
        (config: ClientConfig | undefined) => parseBannerRules(config?.settings.banners?.rulesJson),
        [],
    );
    const { clientId, value, setValue, initial, loadError, saveError, saving, save, reload } =
        useJsonSection<BannerRule[]>(extract);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-banners-load-error" />;
    }
    if (value === null || initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    const rules = value;
    const updateRule = (idx: number, patch: Partial<BannerRule>) =>
        setValue(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const removeRule = (idx: number) => setValue(rules.filter((_, i) => i !== idx));

    const dirty = JSON.stringify(initial) !== JSON.stringify(value);
    const valid = rules.every((r) => r.id.trim() && r.message.trim() && r.thresholdDays >= 0);

    const handleSave = () =>
        save([{ key: BANNERS_RULES_KEY, value: JSON.stringify({ rules }), valueType: 'json' }], value);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <CopyFromClientButton targetClientId={clientId} groups={['banners']} onCopied={reload} />
            <Form layout="vertical" data-testid="settings-banners-form">
                {rules.map((rule, idx) => (
                    <Space
                        key={idx}
                        align="end"
                        wrap
                        style={{ display: 'flex', marginBottom: 8 }}
                        data-testid={`settings-banners-rule-${idx}`}
                    >
                        <Form.Item label="Type" style={{ marginBottom: 0 }}>
                            <Select
                                style={{ width: 170 }}
                                value={rule.type}
                                onChange={(v) => updateRule(idx, { type: v })}
                                options={[{ value: 'membershipExpiry', label: 'Membership expiry' }]}
                            />
                        </Form.Item>
                        <Form.Item label="Within (days)" style={{ marginBottom: 0 }}>
                            <InputNumber
                                inputMode="numeric"
                                min={0}
                                value={rule.thresholdDays}
                                onChange={(v) => updateRule(idx, { thresholdDays: typeof v === 'number' ? v : 0 })}
                            />
                        </Form.Item>
                        <Form.Item label="Severity" style={{ marginBottom: 0 }}>
                            <Select
                                style={{ width: 150 }}
                                value={rule.severity}
                                onChange={(v) => updateRule(idx, { severity: v })}
                                options={SEVERITY_OPTIONS}
                            />
                        </Form.Item>
                        <Form.Item label="Message" style={{ marginBottom: 0 }}>
                            <Input
                                style={{ width: 320 }}
                                value={rule.message}
                                onChange={(e) => updateRule(idx, { message: e.target.value })}
                            />
                        </Form.Item>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeRule(idx)}
                            data-testid={`settings-banners-remove-${idx}`}
                        />
                    </Space>
                ))}
                <Button onClick={() => setValue([...rules, newRule()])} data-testid="settings-banners-add">
                    Add rule
                </Button>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Use {'{{daysUntilExpiry}}'} in the message to insert the number of days remaining.
                </Text>
            </Form>
            {saveError && <Alert type="error" message={saveError} data-testid="settings-banners-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-banners-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-banners-discard-btn"
                    onClick={() => setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
